import { useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider } from './auth/AuthProvider'
import { AuthCallback } from './auth/AuthCallback'
import { loadSession } from './auth/session'
import { RETURN_TO_PARAM, sanitizeReturnTo, useSessionExpiryRedirect } from './auth/sessionExpiry'
import { getProfile } from './auth/cognitoProfile'
import { DataProvider } from './data/DataProvider'
import { Shell as AppShell, MOBILE_BREAKPOINT } from './components/Shell'
import { OfflineBanner } from './components/OfflineBanner'
import { NearestCityPrompt } from './components/NearestCityPrompt'
import { storePendingJoinConfirm, storePendingProfileLink, takePendingClaim, takePendingJoinConfirm, takePendingProfileLink } from './data/myApi'
import { countIssued, countNowSteps } from './data/getReady'
import { ChildConfirmEntry, ChildDetailsEntry, GetReady, GetReadyLiveProvider, HeatEntry, QuestionnaireEntry, useGetReadyLive } from './screens/GetReady'
import { ProfileWizard } from './screens/wizard/ProfileWizard'
import { WaiverFlow } from './screens/waiver/WaiverFlow'
import { Orders } from './screens/Orders'
import { TicketDetail } from './screens/orders/TicketDetail'
import { AssignEntry } from './screens/orders/AssignFlow'
import { Tickets } from './screens/Tickets'
import { Profile } from './screens/Profile'
import { ProfileEditHub } from './screens/ProfileEditHub'
import { Login } from './screens/Login'
import { ClaimLanding } from './screens/ClaimLanding'
import { TransferLanding } from './screens/TransferLanding'
import { transferEnabled } from './lib/transferFlag'
import { brandSignupEnabled } from './lib/brandSignupFlag'
import { JoinSignup } from './screens/JoinSignup'
import { JoinConfirm } from './screens/JoinConfirm'
import { profileLinksEnabled } from './lib/profileLinksFlag'
import { CompleteProfile } from './screens/CompleteProfile'
import { teenSelfHolderEnabled } from './lib/teenSelfHolderFlag'
import { GuardianSign, ensureNoReferrerMeta } from './screens/GuardianSign'
import { peekPendingGuardianSign } from './data/myApi'

function Shell() {
  // SESSION END ⇒ VISIBLE redirect to sign-in, return path preserved
  // (CMT-2026-09-03-026): route-entry / expiry-timer / tab-wake watcher for the
  // whole authed tree. Composes with the self-heal lane — it refreshes first
  // and only redirects when the session is genuinely dead (sessionExpiry.ts).
  useSessionExpiryRedirect()

  // Viewport is auto-detected from the real window width — no manual toggle
  // (the handoff's Mobile/Desktop switch is a prototype affordance, not UI).
  // Breakpoint per the V1.9.1 handoff: desktop >= 900px, else mobile.
  const [mobile, setMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT)

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches)
    mql.addEventListener('change', onChange)
    setMobile(mql.matches)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  // Real account-holder name for the mobile account tooltip pattern
  // `{firstName} {lastName} — your account` (canon §3.2; 'Alex Morgan' in the
  // DC is [demo]). Real Cognito attributes only — never a fabricated name.
  const [accountName, setAccountName] = useState<string | undefined>()
  useEffect(() => {
    const session = loadSession()
    if (!session) return
    let cancelled = false
    getProfile(session.accessToken)
      .then(p => {
        if (cancelled) return
        const name = [p.givenName, p.familyName].filter(Boolean).join(' ')
        if (name) setAccountName(name)
      })
      .catch(() => { /* name stays unset — tooltip falls back, no fabrication */ })
    return () => { cancelled = true }
  }, [])

  // Badge = count of the participant's NON-system `now` steps across all
  // events, DERIVED per canon §2.4 from the SAME live source as the card pills
  // (countNowSteps in src/data/getReady.ts, over the shared GetReadyLiveProvider
  // result — audit D8). 0 correctly hides the badge; nothing is invented.
  const { events: liveEvents } = useGetReadyLive()
  const badgeCount = countNowSteps(liveEvents)
  // Tickets badge = number of ISSUED tickets available in the wallet, DERIVED
  // from the SAME live source as the Get-ready badge (countIssued in
  // src/data/getReady.ts) so the green count and the wallet pager always agree
  // (BOARDROOM-2026-08-14-100859 UX #4). 0 hides the badge.
  const ticketsBadgeCount = countIssued(liveEvents)

  return (
    <AppShell mobile={mobile} badgeCount={badgeCount} ticketsBadgeCount={ticketsBadgeCount} accountName={accountName}>
      {/* Route map per canon §1.2 (PROPOSED — Duncan to ratify, §12.1).
          Help is an overlay/sheet toggled in place — NEVER a route. */}
      {/* "Is {city} your nearest city?" (Duncan ruling 2026-10-07): renders
          only on the tab home screens, only when my-api flags the city as
          filled in by legacy data cleaning. */}
      <NearestCityPrompt />
      <Routes>
        {/* Shell default: / → redirect /get-ready */}
        <Route path="/" element={<Navigate to="/get-ready" replace />} />
        {/* Profile wizard (self) — canon §1.2:
              /get-ready/profile/step-1..5, /get-ready/profile/complete
              /get-ready/emergency → opens the wizard at step 4.
            TODO(V1.1 kids amendment §12.2): managed-child wizard routes
            /get-ready/profiles/:childKey/step-1..5 + /complete land with the
            expected kids canon amendment — deliberately NOT built this cycle. */}
        <Route path="/get-ready/profile" element={<Navigate to="/get-ready/profile/step-1" replace />} />
        <Route path="/get-ready/profile/:stepSlug" element={<ProfileWizard />} />
        <Route path="/get-ready/emergency" element={<Navigate to="/get-ready/profile/step-4" replace />} />
        {/* Waiver flow — canon §1.2/§6:
              /get-ready/waiver/:eventId/doc-1..N, /sign, /receipt.
            Documents come from the settings-owned template seam (ruling E);
            without a delivered template the flow renders its honest
            "not available yet" state — nothing is hardcoded. */}
        <Route path="/get-ready/waiver/:eventId" element={<Navigate to="doc-1" replace />} />
        <Route path="/get-ready/waiver/:eventId/:stepSlug" element={<WaiverFlow />} />
        {/* Heat-select route for the REQUIRED participant heat choice. Heat is
            a participant step in the OnePulse canon (label "Heat selection",
            CTA "Choose") — NOT system-automatic; an earlier note here claimed
            the opposite and was corrected per the CMT-2026-08-09-043 audit (D1). */}
        <Route path="/get-ready/heat/:eventId" element={<HeatEntry />} />
        {/* Child-confirm step for a GUARDIAN-CLAIMED seat (S2 slice,
            BOARDROOM-2026-09-03-052111 / CMT-2026-09-03-020): the parent
            confirms the child's name and enters DOB + gender — the child's
            identity home on the guardian journey. */}
        <Route path="/get-ready/child-confirm/:eventId" element={<ChildConfirmEntry />} />
        {/* Server-backed child details, keyed by TICKET (both child lanes). */}
        <Route path="/get-ready/child-details/:ticketId" element={<ChildDetailsEntry />} />
        {/* Event questionnaire (SETTINGS-2026-08-25-105204 via the my-api BFF,
            agreed contract MY-2026-08-25-054534). The step renders on the
            get-ready card ONLY while settings reports active+show+!submitted. */}
        <Route path="/get-ready/questionnaire/:eventId" element={<QuestionnaireEntry />} />
        {/* Orders — canon §1.2/§7: /orders (list) · /orders/:orderRef
            (expanded card) · ticket detail (seats) · assign flow (wizard
            sub-steps are in-flow state, not routed). */}
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:orderRef" element={<Orders />} />
        <Route path="/orders/:orderRef/tickets/:ticketId" element={<TicketDetail />} />
        <Route path="/orders/:orderRef/tickets/:ticketId/seats/:seatNo/assign" element={<AssignEntry />} />
        {/* Tickets wallet — canon §1.2/§8: /tickets (wallet) · specific stub
            /tickets/:passId (opens the pager on that issued ticket). */}
        <Route path="/tickets" element={<Tickets />} />
        <Route path="/tickets/:passId" element={<Tickets />} />
        {/* Profile flow, CHILD subject (V1.9.1 P3, README §Flows B): a managed
            child's five steps, subject-scoped drafts (`child_{key}` records).
            The STATIC /profile/children/new (Profile's add-child panel) must
            outrank the :childKey param — 'new' is never a child key. */}
        {/* Account edit hub (CMT-2026-09-03-026): the Profile-complete
            summary card as an ACCOUNT HUB — own card + a card per managed
            child, per-row pencils deep-linking into the wizard (`?hub=1`
            returns here on save/back). Edit on the Profile account card
            lands here — never a blank stepper. */}
        <Route path="/profile/edit" element={<ProfileEditHub />} />
        <Route path="/profile/children/new" element={<Profile />} />
        {/* Bare child route → the wizard redirects itself to step-1. */}
        <Route path="/profile/children/:childKey" element={<ProfileWizard />} />
        <Route path="/profile/children/:childKey/:stepSlug" element={<ProfileWizard />} />
        {/* Each section's wildcard resolves to its section screen, so deep
            links never 404. */}
        <Route path="/get-ready/*" element={<GetReady />} />
        <Route path="/orders/*" element={<Orders />} />
        <Route path="/tickets/*" element={<Tickets />} />
        <Route path="/profile/*" element={<Profile />} />
        {/* Brand signup confirmation (design §8; VITE_BRAND_SIGNUP_ENABLED).
            Flag off ⇒ no route: the catch-all below takes it. */}
        {brandSignupEnabled() && <Route path="/join/confirm" element={<JoinConfirm />} />}
        {/* "Complete your profile" email landing (PR-C;
            VITE_PROFILE_LINKS_ENABLED). Flag off ⇒ no route, no request: the
            catch-all below takes it. */}
        {profileLinksEnabled() && <Route path="/complete-profile" element={<CompleteProfile />} />}
        <Route path="*" element={<Navigate to="/get-ready" replace />} />
      </Routes>
    </AppShell>
  )
}

export default function App() {
  // Authed = a REAL Cognito session, nothing else. There is no demo path.
  const authed = loadSession() !== null

  // Friendly branded offline state (BOARDROOM-2026-08-12-120533) — global,
  // above every route so a connectivity loss is handled the same everywhere.
  const content = appContent(authed)
  return (
    <>
      <OfflineBanner />
      {content}
    </>
  )
}

function appContent(authed: boolean) {
  if (window.location.pathname === '/auth/callback') {
    return (
      <AuthProvider>
        <AuthCallback />
      </AuthProvider>
    )
  }

  // Invite claim-landing (OnePulse slice 1) — reachable signed in or out.
  const claimMatch = window.location.pathname.match(/^\/claim\/([A-Za-z0-9-]+)$/)
  if (claimMatch) {
    return (
      <AuthProvider>
        <ClaimLanding token={claimMatch[1]} />
      </AuthProvider>
    )
  }

  // Teen self-holder parent sign page (design MY-DESIGN-JUNIOR-SELF-HOLDER-
  // 1020 §4.3.4; VITE_TEEN_SELF_HOLDER_ENABLED — flag off ⇒ the path falls
  // through to normal routing). PUBLIC like /join/:slug: reachable signed in
  // or out — the page runs its own email check + native email-code sign-in.
  // The no-referrer meta goes in BEFORE the page renders (infra's ask).
  if (teenSelfHolderEnabled()) {
    const gsMatch = window.location.pathname.replace(/\/+$/, '').match(/^\/guardian-sign\/([A-Za-z0-9_-]{20,128})$/)
    if (gsMatch) {
      ensureNoReferrerMeta()
      return <GuardianSign token={gsMatch[1]} />
    }
  }

  // Guest signed-link transfer landing (PROJ-0078-WU-10, decision (c)) —
  // public like /claim/:token, and ONLY behind VITE_TRANSFER_ENABLED; flag off
  // falls through to normal routing. The token is never logged or stored.
  if (transferEnabled()) {
    const transferMatch = window.location.pathname.match(/^\/transfer\/([A-Za-z0-9._-]+)$/)
    if (transferMatch) return <TransferLanding token={transferMatch[1]} />
  }

  // Brand signup (design §8; VITE_BRAND_SIGNUP_ENABLED — flag off ⇒ these
  // paths fall through to normal routing). /join/:slug is the PUBLIC
  // newsletter form, reachable signed in or out. /join/confirm needs a
  // session: signed out, the gate below renders Login AT this path (its
  // returnTo brings the person back), and the token is stashed for the
  // fresh-tab sign-in link / first-sign-in detours.
  if (brandSignupEnabled()) {
    const path = window.location.pathname.replace(/\/+$/, '')
    if (path === '/join/confirm') {
      if (!authed) {
        const tok = new URLSearchParams(window.location.search).get('t')
        if (tok) storePendingJoinConfirm(tok)
      }
    } else {
      const joinMatch = path.match(/^\/join\/([a-z0-9][a-z0-9-]{0,62})$/)
      if (joinMatch) return <JoinSignup slug={joinMatch[1]} />
    }
  }

  // "Complete your profile" link (PR-C; VITE_PROFILE_LINKS_ENABLED): same
  // sign-in-first handling as /join/confirm — signed out, Login renders at
  // this path (returnTo) and the token is stashed for the fresh-tab detours.
  if (profileLinksEnabled() && !authed && window.location.pathname.replace(/\/+$/, '') === '/complete-profile') {
    const tok = new URLSearchParams(window.location.search).get('t')
    if (tok) storePendingProfileLink(tok)
  }

  // A claim stashed before the hosted-UI round trip resumes now that the
  // session exists (AuthCallback always returns to '/').
  if (authed) {
    const pending = takePendingClaim()
    if (pending) {
      window.location.replace(`/claim/${pending}`)
      return null
    }
    // A brand-signup confirm interrupted by sign-in resumes (one-shot; the
    // confirm page itself clears the stash when it is already there).
    if (brandSignupEnabled() && window.location.pathname.replace(/\/+$/, '') !== '/join/confirm') {
      const join = takePendingJoinConfirm()
      if (join) {
        window.location.replace(`/join/confirm?t=${encodeURIComponent(join)}`)
        return null
      }
    }
    // A guardian sign interrupted by a sign-in detour (fresh tab / first-
    // sign-in wizard) resumes; the page itself consumes the stash on load.
    if (teenSelfHolderEnabled()) {
      const gs = peekPendingGuardianSign()
      if (gs) {
        window.location.replace(`/guardian-sign/${encodeURIComponent(gs)}`)
        return null
      }
    }
    // A complete-profile link interrupted by sign-in resumes (one-shot; the
    // page itself clears the stash when it is already there).
    if (profileLinksEnabled() && window.location.pathname.replace(/\/+$/, '') !== '/complete-profile') {
      const link = takePendingProfileLink()
      if (link) {
        window.location.replace(`/complete-profile?t=${encodeURIComponent(link)}`)
        return null
      }
    }
    // Session-expiry round-trip (CMT-2026-09-03-026): landing authed on
    // '/?returnTo=…' (e.g. auth completed in another tab after a dead-session
    // redirect) resumes the preserved path. Login's own finish() handles the
    // normal same-tab case; this covers the races. Validated — never an
    // off-origin or callback target.
    if (window.location.pathname === '/') {
      const back = sanitizeReturnTo(new URLSearchParams(window.location.search).get(RETURN_TO_PARAM))
      if (back) {
        window.location.replace(back)
        return null
      }
    }
  }

  if (!authed) {
    return (
      <AuthProvider>
        <Login />
      </AuthProvider>
    )
  }

  return (
    <AuthProvider>
      <DataProvider>
        {/* One shared live Get-ready subscription above the shell so the nav
            badge derives from the same data as the screen (audit D8). */}
        <GetReadyLiveProvider>
          <Shell />
        </GetReadyLiveProvider>
      </DataProvider>
    </AuthProvider>
  )
}
